import React from 'react';
import { Link } from 'react-router-dom';
import './FunnelUI.css';

export function FunnelPage({ children, className = '' }) {
  return <main className={`funnel-page ${className}`.trim()}>{children}</main>;
}

export function FunnelSection({
  children,
  className = '',
  id,
  tone = 'base',
}) {
  return (
    <section id={id} className={`funnel-section funnel-section--${tone} ${className}`.trim()}>
      <div className="funnel-container">{children}</div>
    </section>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  copy,
  align = 'center',
  className = '',
}) {
  return (
    <div className={`funnel-section-header funnel-section-header--${align} ${className}`.trim()}>
      {eyebrow && <p className="funnel-eyebrow">{eyebrow}</p>}
      <h2>{title}</h2>
      {copy && <p>{copy}</p>}
    </div>
  );
}

export function ButtonLink({
  children,
  to,
  href,
  variant = 'primary',
  className = '',
  target,
  rel,
  ...props
}) {
  const classes = `funnel-button funnel-button--${variant} ${className}`.trim();

  if (to) {
    return (
      <Link className={classes} to={to} {...props}>
        {children}
      </Link>
    );
  }

  if (href) {
    const isExternal = /^https?:\/\//.test(href);
    return (
      <a
        className={classes}
        href={href}
        target={target || (isExternal ? '_blank' : undefined)}
        rel={rel || (isExternal ? 'noopener noreferrer' : undefined)}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <button className={classes} type="button" {...props}>
      {children}
    </button>
  );
}

export function CtaRow({ children, align = 'start', className = '' }) {
  return (
    <div className={`funnel-cta-row funnel-cta-row--${align} ${className}`.trim()}>
      {children}
    </div>
  );
}

export function Hero({
  eyebrow,
  title,
  copy,
  actions,
  media,
  stats,
  className = '',
}) {
  return (
    <FunnelSection className={`funnel-hero ${className}`.trim()} tone="hero">
      <div className="funnel-hero-grid">
        <div className="funnel-hero-copy">
          {eyebrow && <p className="funnel-eyebrow">{eyebrow}</p>}
          <h1>{title}</h1>
          {copy && <p className="funnel-hero-lede">{copy}</p>}
          {actions && <CtaRow>{actions}</CtaRow>}
          {stats && <div className="funnel-hero-stats">{stats}</div>}
        </div>
        {media && <div className="funnel-hero-media">{media}</div>}
      </div>
    </FunnelSection>
  );
}

export function Card({ eyebrow, title, copy, children, actions, className = '' }) {
  return (
    <article className={`funnel-card ${className}`.trim()}>
      {eyebrow && <p className="funnel-card-eyebrow">{eyebrow}</p>}
      <h3>{title}</h3>
      {copy && <p>{copy}</p>}
      {children}
      {actions && <div className="funnel-card-actions">{actions}</div>}
    </article>
  );
}

export function StatCard({ label, value, note }) {
  return (
    <div className="funnel-stat-card">
      <strong>{value}</strong>
      <span>{label}</span>
      {note && <small>{note}</small>}
    </div>
  );
}

export function CardGrid({ children, columns = 3, className = '' }) {
  return (
    <div className={`funnel-card-grid funnel-card-grid--${columns} ${className}`.trim()}>
      {children}
    </div>
  );
}

export function ProofMedia({ src, alt, label, type = 'image' }) {
  return (
    <figure className="funnel-proof-media">
      {type === 'video' ? (
        <video src={src} poster={alt ? undefined : src} muted loop autoPlay playsInline />
      ) : (
        <img src={src} alt={alt} />
      )}
      {label && <figcaption>{label}</figcaption>}
    </figure>
  );
}

export function StepList({ steps, className = '' }) {
  return (
    <ol className={`funnel-step-list ${className}`.trim()}>
      {steps.map((step, index) => (
        <li key={`${step.title}-${index}`}>
          <span>{String(index + 1).padStart(2, '0')}</span>
          <div>
            <strong>{step.title}</strong>
            <p>{step.copy}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function Alert({ children, tone = 'info', className = '' }) {
  return <div className={`funnel-alert funnel-alert--${tone} ${className}`.trim()}>{children}</div>;
}
